import { useCanManageCompany } from "@/hooks/useCanManageCompany";
import { useMemo } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Clock, ShieldCheck } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useIsSuperadmin } from "@/hooks/useUserRole";
import { SettingsBackLink } from "@/components/settings/SettingsBackLink";

interface EstimateRow {
  phase_category: string;
  appliance_type: string | null;
  brand: string | null;
  sample_size: number;
  median_seconds: number;
  p75_seconds: number;
  avg_seconds: number;
  last_logged_at: string;
}

// PHASE_LABELS retained for fallback only; primary source is i18n phaseTimeAnalytics.phaseLabels.*
const PHASE_LABELS: Record<string, string> = {
  triage_assessment: "Triage Assessment",
  refurbish_start: "Refurbish Start",
  error_codes: "Common Error Codes",
  troubleshooting: "Pre-repair Troubleshooting",
  parts_replacement: "Parts Replacement Guides",
  verification: "Final Verification",
  cleaning: "Cleaning",
};

function formatDuration(seconds: number): string {
  if (!seconds || seconds < 0) return "—";
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}

export default function PhaseTimeAnalytics() {
  const { t } = useTranslation("admin");
  const navigate = useNavigate();
  const { allowed: isAdmin, isLoading: roleLoading } = useCanManageCompany();
  const { isSuperadmin } = useIsSuperadmin();

  const { data, isLoading } = useQuery({
    queryKey: ["phase-time-estimates"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("phase_time_estimates" as any)
        .select("*")
        .order("phase_category");
      if (error) throw error;
      return (data || []) as unknown as EstimateRow[];
    },
    enabled: isAdmin || isSuperadmin,
  });

  const grouped = useMemo(() => {
    const map = new Map<string, EstimateRow[]>();
    (data || []).forEach((row) => {
      const key = row.phase_category;
      const arr = map.get(key) || [];
      arr.push(row);
      map.set(key, arr);
    });
    return map;
  }, [data]);

  if (roleLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }
  // Access enforced by the route guard in App.tsx.

  return (
    <div className="min-h-screen bg-background pb-24">
      <div className="px-6 pt-8 pb-6">
        <SettingsBackLink to="/settings/data" label={t("nav.data", { ns: "settings" })} />
        <div className="flex items-center gap-2 mb-1">
          <Clock className="w-5 h-5 text-primary" />
          <h1 className="text-2xl font-bold text-foreground">{t("phaseTime.title")}</h1>
        </div>
        <p className="text-muted-foreground">
          {t("phaseTimeAnalytics.subtitle")}
        </p>
        <div className="mt-4 flex items-start gap-2 rounded-lg bg-muted/50 border border-border p-3 text-sm text-muted-foreground">
          <ShieldCheck className="w-4 h-4 text-primary mt-0.5 shrink-0" />
          <div>
            <p className="font-medium text-foreground">{t("phaseTimeAnalytics.anonymousTitle")}</p>
            <p className="text-xs">{t("phaseTimeAnalytics.anonymousBody")}</p>
          </div>
        </div>
      </div>

      <div className="px-6 space-y-6">
        {isLoading ? (
          <div className="text-center py-12 text-muted-foreground text-sm">{t("phaseTimeAnalytics.loading")}</div>
        ) : (data?.length ?? 0) === 0 ? (
          <div className="bg-card border border-border rounded-xl p-8 text-center text-muted-foreground text-sm">
            {t("phaseTimeAnalytics.noData")}
          </div>
        ) : (
          Array.from(grouped.entries()).map(([phaseCategory, rows]) => (
            <div key={phaseCategory} className="bg-card rounded-xl border border-border overflow-hidden">
              <div className="px-5 py-3 border-b border-border">
                <h2 className="font-semibold text-foreground">
                  {t(`phaseTimeAnalytics.phaseLabels.${phaseCategory}`, { defaultValue: PHASE_LABELS[phaseCategory] || phaseCategory })}
                </h2>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-muted/30 text-xs text-muted-foreground">
                      <th className="px-4 py-2 text-left font-medium">{t("phaseTimeAnalytics.applianceType")}</th>
                      <th className="px-4 py-2 text-left font-medium">{t("phaseTimeAnalytics.brand")}</th>
                      <th className="px-4 py-2 text-right font-medium">{t("phaseTimeAnalytics.median")}</th>
                      <th className="px-4 py-2 text-right font-medium">{t("phaseTimeAnalytics.p75")}</th>
                      <th className="px-4 py-2 text-right font-medium">{t("phaseTimeAnalytics.average")}</th>
                      <th className="px-4 py-2 text-right font-medium">{t("phaseTimeAnalytics.samples")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r, i) => (
                      <tr key={i} className="border-t border-border">
                        <td className="px-4 py-2 text-foreground">{r.appliance_type || "—"}</td>
                        <td className="px-4 py-2 text-muted-foreground">{r.brand || t("phaseTimeAnalytics.anyBrand")}</td>
                        <td className="px-4 py-2 text-right tabular-nums text-foreground">
                          {formatDuration(r.median_seconds)}
                        </td>
                        <td className="px-4 py-2 text-right tabular-nums text-muted-foreground">
                          {formatDuration(r.p75_seconds)}
                        </td>
                        <td className="px-4 py-2 text-right tabular-nums text-muted-foreground">
                          {formatDuration(r.avg_seconds)}
                        </td>
                        <td className="px-4 py-2 text-right tabular-nums text-muted-foreground">
                          {r.sample_size}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
